import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { TopBar } from '@/components/layout/TopBar';
import { PageContainer } from '@/components/layout/PageContainer';
import { useRequestEmailCode } from '@/api/queries/auth';
import { isEmail } from '@/utils/validators';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';

export function LoginEmailPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const req = useRequestEmailCode();
  const pushToast = useUIStore((s) => s.pushToast);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmail(email)) {
      setError(t('errors.validation'));
      return;
    }
    setError(null);
    try {
      await req.mutateAsync(email);
      navigate(paths.loginOtp, { state: { email } });
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <PageContainer>
      <TopBar back title={t('auth.email.title')} />
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          autoFocus
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t('auth.email.placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          aria-label={t('auth.email.title')}
        />
        <Button type="submit" block size="lg" variant="inverted" loading={req.isPending}>
          {t('auth.email.send_code')}
        </Button>
      </form>
    </PageContainer>
  );
}
